import { css, html, nothing } from "lit";
import { query, queryAll } from "lit/decorators.js";
import { ifDefined } from "lit-html/directives/if-defined.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { getTextLabelClasses, getHiddenLabelClasses, getSelectClasses, getArrowIconClasses, getIconClasses, getXIconClasses } from "./utils.js";
import "../Badge/index.js";
import "../Tag/index.js";
import "../../atom/Icon/index.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
const sizes = ["medium", "small", "smaller"];
let DsfSelectButton = class extends BaseComponent {
  constructor() {
    super(...arguments);
    this.dsf_options = [];
    this._group_position = "ungrouped";
    this.disabled = false;
    this.dsf_open = false;
    this.dsf_error = false;
    this.dsf_success = false;
    this.dsf_size = "medium";
    this.dsf_multiselect = false;
    this.dsf_show_clear_icon = false;
  }
  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));
    this.resizeObserver.observe(this);
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    (_a = this.resizeObserver) == null ? void 0 : _a.disconnect();
    this.resizeObserver = void 0;
  }
  async updated(changedProps) {
    super.updated(changedProps);
    await this.updateComplete;
    this.updateHiddenCount();
  }
  updateHiddenCount() {
    if (!this.dsf_multiselect) {
      return;
    }
    const { badges, badgesContainer, hiddenCountLabel } = this;
    if (!badgesContainer || !hiddenCountLabel) {
      return;
    }
    hiddenCountLabel.style.display = "none";
    badges.forEach((el) => el.style.display = "");
    let totalWidth = 0;
    for (let i = 0; i < badges.length; i++) {
      totalWidth += badges[i].offsetWidth;
      const elementsOverflow = totalWidth - (badgesContainer.offsetWidth - (badges.length - 1) * 4);
      if (elementsOverflow > 1) {
        hiddenCountLabel.style.visibility = "hidden";
        hiddenCountLabel.style.display = "";
        hiddenCountLabel.innerText = `${badges.length - (i - 1)}`;
        const counterWidth = hiddenCountLabel.offsetWidth;
        hiddenCountLabel.style.display = "none";
        hiddenCountLabel.style.visibility = "";
        const hideFrom = Math.max(
          badges[i].offsetWidth - elementsOverflow > counterWidth ? i : i - 1,
          1
        );
        const hiddenElements = [...badges].slice(hideFrom);
        if (hiddenElements.length > 0) {
          hiddenElements.forEach((el) => el.style.display = "none");
          hiddenCountLabel.innerText = `${badges.length - hideFrom}`;
          hiddenCountLabel.title = hiddenElements.map((b) => b.innerText.trim()).join(", ");
          hiddenCountLabel.style.display = "";
        }
        break;
      }
    }
  }
  focus() {
    this.rootElement.focus();
  }
  handleResize() {
    this.updateHiddenCount();
  }
  onUnselect(event, value) {
    event.preventDefault();
    event.stopPropagation();
    this.emit({ name: "unselect-option", payload: value });
  }
  handleClearMouseDown(event) {
    event.stopPropagation();
  }
  handleClearClick() {
    if (this.disabled) {
      return;
    }
    this.emit("clear-click");
  }
  renderSelectContent() {
    var _a;
    const textLabelClasses = getTextLabelClasses(this);
    if (!this.dsf_options.length) {
      return html`<span class=${textLabelClasses}>${this.dsf_placeholder}</span>`;
    }
    if (!this.dsf_multiselect) {
      return html`<span class=${`single-selected-value ${textLabelClasses}`}
        >${(_a = this.dsf_options[0].icon) == null ? void 0 : _a.cloneNode()}
        <span class="truncate">${this.dsf_options[0].label}</span></span
      >`;
    }
    const hiddenLabelClasses = getHiddenLabelClasses(this);
    return html`
      <div id="badges" class="flex h-[100%] flex-auto items-center gap-4 overflow-hidden">
        ${this.dsf_options.map(
      (option) => html` <dsf-tag
            @close=${(event) => this.onUnselect(event, option.value)}
            .disabled=${this.disabled}
            data-badge
            .dsf_closable=${!this.disabled}
            dsf_size=${this.dsf_size === "medium" ? "small" : "smaller"}
          >
            ${option.label}
          </dsf-tag>`
    )}
        <div id="hiddenCountLabel" class=${hiddenLabelClasses}></div>
      </div>
    `;
  }
  render() {
    const selectClasses = getSelectClasses(this);
    const arrowIconClasses = getArrowIconClasses(this);
    const iconClasses = getIconClasses(this);
    const selectContent = this.renderSelectContent();
    const xIconClasses = getXIconClasses(this);
    const icon = this.dsf_show_clear_icon && this.dsf_options.length ? html`<dsf-icon
            dsf_name="X"
            class=${xIconClasses}
            @click=${this.handleClearClick}
            @mousedown=${this.handleClearMouseDown}
          ></dsf-icon>` : html`<dsf-icon dsf_name="ChevronDown" class=${arrowIconClasses}></dsf-icon>`;
    return html` <div
      id="root"
      class=${selectClasses}
      tabindex=${ifDefined(this.disabled ? void 0 : 0)}
      part="select-button"
    >
      ${this.dsf_icon ? html`<dsf-icon dsf_name=${this.dsf_icon} class=${iconClasses}></dsf-icon>` : nothing}
      ${selectContent} ${icon}
    </div>`;
  }
};
DsfSelectButton.styles = [
  ...BaseComponent.styles,
  css`
      .single-selected-value > dsf-icon {
        width: calc(var(--dsf-base-size) * 1.6);
        height: calc(var(--dsf-base-size) * 1.6);
      }
    `
];
__decorateClass([
  prop({ type: Array })
], DsfSelectButton.prototype, "dsf_options", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectButton.prototype, "_group_position", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectButton.prototype, "dsf_placeholder", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectButton.prototype, "disabled", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectButton.prototype, "dsf_open", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectButton.prototype, "dsf_error", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectButton.prototype, "dsf_success", 2);
__decorateClass([
  prop({ type: String, reflect: true, validator: (value) => sizes.includes(value) })
], DsfSelectButton.prototype, "dsf_size", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectButton.prototype, "dsf_multiselect", 2);
__decorateClass([
  prop({ type: String })
], DsfSelectButton.prototype, "dsf_icon", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfSelectButton.prototype, "dsf_show_clear_icon", 2);
__decorateClass([
  query("#badges")
], DsfSelectButton.prototype, "badgesContainer", 2);
__decorateClass([
  queryAll("[data-badge]")
], DsfSelectButton.prototype, "badges", 2);
__decorateClass([
  query("#hiddenCountLabel")
], DsfSelectButton.prototype, "hiddenCountLabel", 2);
__decorateClass([
  query("#root")
], DsfSelectButton.prototype, "rootElement", 2);
DsfSelectButton = __decorateClass([
  defineElement("dsf-select-button")
], DsfSelectButton);
export {
  DsfSelectButton,
  sizes
};
